'use client';

import { InverseHeader } from '@jouzen/ecom-components/layout';
import { useLocale, useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';

import { EventType } from '@/analytics/types';
import ArticleCardsModule from '@/app/components/ArticleCardsModule';
import ArticleCardTag from '@/app/components/ArticleCardsModule/ArticleCardTag';
import FooterCta from '@/app/components/FooterCta';
import useScrollTracking from '@/app/hooks/useScrollTracking';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

import AccuracySection from './_components/AccuracySection';
import MachineLearningJumbotron from './_components/MachineLearningJumbotron';
import MeasureSection from './_components/MeasureSection';
import PublicationsSection from './_components/PublicationsSection';
import ResearchInnovationSection from './_components/ResearchInnovationSection';
import ScienceAndResearchAdvisor from './_components/ScienceAndResearchAdvisor';
import ScienceAndResearchCallout from './_components/ScienceAndResearchCallout';
import ScienceAndResearchCards from './_components/ScienceAndResearchCards';
import ScienceAndResearchFooter from './_components/ScienceAndResearchFooter';
import ScienceAndResearchHero from './_components/ScienceAndResearchHero';
import ScienceAndResearchInnovation from './_components/ScienceAndResearchInnovation';
import ScienceAndResearchIntegrations from './_components/ScienceAndResearchIntegrations';
import ScienceAndResearchJumbotron from './_components/ScienceAndResearchJumbotron';
import ScienceAndResearchMedicalAdvisors from './_components/ScienceAndResearchMedicalAdvisors';
import ScienceAndResearchUniversities from './_components/ScienceAndResearchUniversities';
import ValidationSection from './_components/ValidationSection';
import {
  scienceAndResearchArticleCards,
  scienceAndResearchArticleImageData,
} from './_data/scienceAndResearchArticlesData';
import useSAndR2025 from './_hooks/useSAndR2025';

const Content = () => {
  // scroll depth tracking hook for segment analytics
  useScrollTracking();
  const t = useTranslations();
  const isRolloutEnabled = useSAndR2025();
  const locale = useLocale();
  const articleImage = scienceAndResearchArticleImageData(
    getLocalePartialUrl(locale),
  );
  const articleCards = scienceAndResearchArticleCards(
    getLocalePartialUrl(locale),
  );

  const getPath = (event: MouseEvent<HTMLAnchorElement>): string | null => {
    const target = event.currentTarget;
    return target.getAttribute('href');
  };

  const sendAnalytics = async (event: MouseEvent<HTMLAnchorElement>) => {
    const path = getPath(event);
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'view',
      location: 'body',
      path,
    });
  };

  const handleClick = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();
    const path = getPath(event);
    await sendAnalytics(event);
    window.open(path!, '_blank');
  };

  const handleFooterClick = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    await sendAnalytics(event);
  };

  return (
    <>
      <ScienceAndResearchHero />
      {isRolloutEnabled ? (
        <>
          <ResearchInnovationSection />
          <AccuracySection />
          <ValidationSection />
          <MeasureSection />
          <PublicationsSection />
        </>
      ) : (
        <>
          <ScienceAndResearchInnovation />
          <ScienceAndResearchIntegrations />
          <ScienceAndResearchCallout />
          <MachineLearningJumbotron />
          <ScienceAndResearchAdvisor />
          <ScienceAndResearchJumbotron />
          <ScienceAndResearchCards />
        </>
      )}
      <ScienceAndResearchUniversities />
      <InverseHeader enter>
        {isRolloutEnabled && (
          <ArticleCardsModule
            articleImage={articleImage}
            articleCards={articleCards}
            data-cy="science-and-research-article-cards"
            eyebrowProps={{
              className: 'mb-8 tracking-tighter!',
              Element: 'h3',
              variant: 'h4',
              weight: 'normal',
            }}
            eyebrowText="science_research_2025_highlights_title"
            eyebrowTextColor="light"
            imageTag={() => (
              <ArticleCardTag
                tag="sleep_and_rest_cta_tag"
                textColor="text-brown-500"
              />
            )}
            mobileSliderAriaLabels={[
              t('science_research_2025_article_one_headline'),
              t('science_research_2025_article_two_headline'),
              t('science_research_2025_article_three_headline'),
            ]}
            onClick={handleClick}
            swipeableDotsProps={{
              activeColor: 'sandstone-200',
              color: 'sandstone-500',
            }}
            wrapperClass="bg-neutral-800 text-sandstone-500 pt-20! pb-0!"
          />
        )}
        <ScienceAndResearchMedicalAdvisors className="py-20" />
        {!isRolloutEnabled && <ScienceAndResearchFooter />}
      </InverseHeader>
      {isRolloutEnabled && (
        <FooterCta
          buttons={[
            {
              className: 'xl:mt-8',
              href: '/contact',
              label: 'business_contact_us',
              onClick: handleFooterClick,
              variant: 'secondary-dark',
            },
            {
              className: 'xl:mt-8',
              href: '/business',
              label: 'oura_for_business',
              onClick: handleFooterClick,
              variant: 'outlined-dark',
            },
          ]}
          className="pt-20"
          description="science_research_2025_research_banner_subtitle"
          image={{
            localizedAlt: 'clinical_studies_image_alt',
            src: '/blue-sky/science-and-research/science-research-clinical-studies',
          }}
          title="science_research_2025_research_banner_title"
          titleProps={{
            variant: 'h4',
            className: 'mb-6 leading-tight tracking-tighter! md:justify-start',
          }}
          variant={{
            sm: 'square',
            lg: 'reverse_two_column',
          }}
        />
      )}
    </>
  );
};

export default Content;
